import { supabase } from '@common/api/authService';
import {
    useEffect,
    useMemo,
    useState
} from 'react';
import { useSupaSocket } from '../../hooks/useSupaSocket';

export interface ResourceSyncStatus {
    resource_type: string;
    status: string;
    updated_at: string;
    error_message?: string;
}

export interface SyncStatusState {
    syncStatuses: ResourceSyncStatus[];
    loading: boolean;
    syncCompleted: boolean;
}

export const useSyncStatus = (workspaceId: string | undefined): SyncStatusState => {
    const [syncStatus, setSyncStatus] = useState<Omit<SyncStatusState, 'syncCompleted'>>({
        syncStatuses: [],
        loading: true,
    });

    useEffect(() => {
        if (!workspaceId) {
            setSyncStatus(prev => ({ ...prev, loading: false }));
            return;
        }

        const fetchInitialStatus = async () => {
            try {
                // 拉取所有同步数据，不区分 resourceType
                const { data, error } = await supabase
                    .schema('data')
                    .from('sync_jobs_log')
                    .select('resource_type, status, updated_at, error_message')
                    .eq('workspace_id', workspaceId)
                    .order('updated_at', { ascending: false });

                if (error) {
                    console.error('Error fetching sync status:', error);
                    setSyncStatus(prev => ({ ...prev, loading: false }));
                    return;
                }

                setSyncStatus({
                    syncStatuses: data || [],
                    loading: false
                });
            } catch (error) {
                console.error('Error fetching sync status:', error);
                setSyncStatus(prev => ({ ...prev, loading: false }));
            }
        };

        fetchInitialStatus();
    }, [workspaceId]);

    const handleRealtimeUpdate = (newRecord: any) => {
        setSyncStatus(prev => {
            const existingIndex = prev.syncStatuses.findIndex(
                s => s.resource_type === newRecord.resource_type
            );

            const updatedRecord: ResourceSyncStatus = {
                resource_type: newRecord.resource_type,
                status: newRecord.status,
                updated_at: newRecord.updated_at,
                error_message: newRecord.error_message
            };

            // 如果已存在该 resource_type 的记录，比较时间戳
            if (existingIndex >= 0) {
                const existing = prev.syncStatuses[existingIndex];
                if (newRecord.updated_at >= existing.updated_at) {
                    const newStatuses = [...prev.syncStatuses];
                    newStatuses[existingIndex] = updatedRecord;
                    return { ...prev, syncStatuses: newStatuses };
                }
                return prev;
            }

            // 如果不存在，添加新记录
            return { ...prev, syncStatuses: [...prev.syncStatuses, updatedRecord] };
        });
    };

    useSupaSocket({
        table: 'sync_jobs_log',
        schema: 'data',
        event: '*',
        enabled: !!workspaceId,
        filter: `workspace_id=eq.${workspaceId}`
    }, {
        onInsert: (payload) => handleRealtimeUpdate(payload.new),
        onUpdate: (payload) => handleRealtimeUpdate(payload.new),
    });

    const syncCompleted = useMemo(() => {
        // 当没有同步状态 -> 同步未完成
        if (syncStatus.syncStatuses.length === 0) {
            return true;
        }

        // 其中有未 Completed 的状态 -> 同步未完成
        const hasIncomplete = syncStatus.syncStatuses.some(s => s.status !== 'COMPLETED');
        return !hasIncomplete;
    }, [syncStatus.syncStatuses]);

    return { ...syncStatus, syncCompleted };
};
